<template>
  <!-- The Studio's first opening (recipient polish 2026-11-03, DESIGN §17):
       four beats in the reveal style, the gold-edged card at the bottom, the
       dots, and the light picking out each part of the Studio above it. The
       words are the built Sun steps. Shown once, in place of Get started. -->
  <div class="st-over" :class="{ rm: reduced }" data-testid="studio-tour">
    <div class="st-stand" aria-hidden="true">
      <div v-for="(b, i) in BEATS" :key="b.tile" :class="['st-tile', { lit: i === at }]" :data-testid="`studio-tour-tile-${i}`">
        <b>{{ b.step }}</b>{{ b.tile }}
      </div>
    </div>
    <div class="st-card" role="dialog" :aria-label="`Your Studio, ${at + 1} of ${BEATS.length}`">
      <button class="st-skip" data-testid="studio-tour-skip" @click="$emit('skip')">Skip the tour</button>
      <div class="st-dots" aria-hidden="true"><i v-for="(b, i) in BEATS" :key="b.tile" :class="{ on: i === at }"></i></div>
      <div class="st-eyebrow">Your Studio · {{ at + 1 }} of {{ BEATS.length }}</div>
      <Transition name="st-fade" mode="out-in">
        <div :key="at">
          <h3 class="st-title" data-testid="studio-tour-title">{{ BEATS[at].title }}</h3>
          <p class="st-p">{{ BEATS[at].body }}</p>
        </div>
      </Transition>
      <div class="st-row">
        <button v-if="at > 0" class="st-back" data-testid="studio-tour-back" @click="at--">Back</button>
        <span v-else></span>
        <button v-if="at < BEATS.length - 1" class="st-btn" data-testid="studio-tour-next" @click="at++">Next</button>
        <button v-else class="st-btn" data-testid="studio-tour-make" @click="$emit('make')">✦ Make my Mystic</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, ref } from 'vue'

defineProps({ reduced: { type: Boolean, default: false } })
const emit = defineEmits(['skip', 'make'])

// The built Sun steps (sky.py BODIES su_*).
const BEATS = [
  { step: 'Become a Mystic', tile: 'Your Mystic', title: 'Make your Mystic', body: 'Your own Mystic, drawn from your chart.' },
  { step: 'Tune its voice', tile: 'Its voice', title: 'Tune its voice', body: 'How your Mystic speaks: plain or poetic, brief or unhurried.' },
  { step: 'Choose its deck', tile: 'Its deck', title: 'Choose its deck', body: 'The deck your Mystic reads from.' },
  { step: 'Share it', tile: 'Share it', title: 'Share it', body: 'Send your Mystic to the people you read for.' },
]
const at = ref(0)

function onKey (e) {
  if (e.key === 'Escape') emit('skip')
  else if (e.key === 'ArrowRight' && at.value < BEATS.length - 1) at.value++
  else if (e.key === 'ArrowLeft' && at.value > 0) at.value--
}
onMounted(() => window.addEventListener('keydown', onKey))
onUnmounted(() => window.removeEventListener('keydown', onKey))
defineExpose({ at })
</script>

<style scoped>
.st-over { position: fixed; inset: 0; z-index: 2100; display: flex; flex-direction: column; align-items: center; justify-content: flex-end; gap: 18px;
  padding: 24px 16px calc(96px + env(safe-area-inset-bottom, 0px)); background: rgba(8,3,20,.72); box-sizing: border-box; }
.st-stand { width: 100%; max-width: 560px; display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
.st-tile { padding: 14px 16px; border-radius: 14px; border: 1px solid rgba(155,134,201,.3); background: rgba(20,7,31,.7); color: #cdbfe6; font: 600 14px/1.3 Inter, system-ui, sans-serif;
  transition: box-shadow .4s, border-color .4s, color .4s; }
.st-tile b { display: block; font: 600 10px Cinzel, serif; letter-spacing: .18em; color: #e7c983; text-transform: uppercase; margin-bottom: 4px; }
.st-tile.lit { border-color: #f0d9a3; color: #fff; box-shadow: 0 0 0 1px #f0d9a3, 0 0 28px rgba(240,217,163,.45); }
.st-card { position: relative; width: 100%; max-width: 460px; box-sizing: border-box; padding: 20px 22px 18px; border-radius: 22px;
  background: radial-gradient(120% 80% at 50% 0%, #2e1857, #150a2b 75%); border: 1px solid rgba(240,217,163,.55); box-shadow: 0 0 60px rgba(240,217,163,.16);
  animation: st-rise .5s ease both; color: #efe4ff; }
.st-skip { position: absolute; right: 14px; top: 12px; background: none; border: 0; color: #cdbfe6; font: 600 13px Inter, system-ui, sans-serif; text-decoration: underline; cursor: pointer; min-height: 32px; }
.st-dots { display: flex; gap: 6px; margin-bottom: 8px; }
.st-dots i { width: 7px; height: 7px; border-radius: 50%; background: rgba(185,166,214,.3); }
.st-dots i.on { background: #f0d9a3; }
.st-eyebrow { font: 600 10px Cinzel, serif; letter-spacing: .2em; text-transform: uppercase; color: #e7c983; }
.st-title { margin: 6px 0 8px; font: 500 28px/1.1 'Cormorant Garamond', Cormorant, serif; color: #fff; }
.st-p { margin: 0 0 14px; font: 400 15px/1.5 Inter, system-ui, sans-serif; color: #e3d8f5; }
.st-row { display: flex; justify-content: space-between; align-items: center; gap: 10px; }
.st-back { min-height: 44px; padding: 8px 18px; border-radius: 12px; border: 1px solid rgba(203,184,238,.4); background: none; color: #e6dafa; font: 600 14px Inter, system-ui, sans-serif; cursor: pointer; }
.st-btn { min-height: 44px; padding: 10px 26px; border: 0; border-radius: 999px; cursor: pointer; background: linear-gradient(#f3dfa8, #c9a94e); color: #22133b;
  font: 600 14px Cinzel, serif; letter-spacing: .08em; box-shadow: 0 6px 18px rgba(201,169,78,.25); }
.st-btn:focus-visible, .st-back:focus-visible, .st-skip:focus-visible { outline: 2px solid #ffe7b5; outline-offset: 2px; }
.st-fade-enter-active, .st-fade-leave-active { transition: opacity .25s; }
.st-fade-enter-from, .st-fade-leave-to { opacity: 0; }
@keyframes st-rise { from { transform: translateY(24px); opacity: 0; } }
@media (max-width: 520px) { .st-stand { grid-template-columns: 1fr; } .st-tile { padding: 10px 14px; } }
@media (prefers-reduced-motion: reduce) { .st-card { animation: st-fadein .4s ease-out both; } .st-tile { transition: none; } }
.rm .st-card { animation: st-fadein .4s ease-out both; }
@keyframes st-fadein { from { opacity: 0; } }
</style>
